import {connect} from 'react-redux';

import {addQueryToPath} from '../../../../../../../libs/shared/helpers/src/browser/urlHelpers';
import {FrontEvent, HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
  wrapInShallowSelector,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {
  buildTagSearchUrl,
  ViewerDropdownInterfaceEventSourcesEnum,
} from '../../../../../../helpers/models/tagHelpers';
import {ResolveDispatchProps} from '../../../../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../../../../models/api/companyModel';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {isConversationSafetyNetRequiredTagging} from '../../../../../../models/api/conversationSafetyNetModel';
import {LegacyConversationLinkDropdownEntryPointModel} from '../../../../../../models/api/legacyEntryPointModel';
import {PermissionsEnum} from '../../../../../../models/api/roleModel';
import {
  ApiSafetyNets,
  SafetyNetRequiredTaggingModel,
  SafetyNetsEnum,
} from '../../../../../../models/api/safetyNetModel';
import {ApiSearchTags, SearchTagModel} from '../../../../../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {TeamModel} from '../../../../../../models/api/teamModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../services/tracking';
import {AppState} from '../../../../../../store/appState';
import {ResourceMap} from '../../../../../../store/appTypes';
import {getHasPermission} from '../../../../../../store/data/access/accessSelectors';
import {getLegacyConversationLinkDropdownEntryPoints} from '../../../../../../store/data/applications/legacyApplicationsSelectors';
import {getCompany} from '../../../../../../store/data/company/companySelectors';
import {
  addTagToConversationsAction,
  bulkAddTagToConversationsAction,
  bulkRemoveTagFromConversationsAction,
  removeTagFromConversationsAction,
} from '../../../../../../store/data/conversations/tagConversationsActions';
import {
  makeGetConversationInboxIdsForTags,
  makeGetConversationTagNamespaces,
} from '../../../../../../store/data/tags/tagsSelectors';
import {getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {getTeamsByNamespace, getUserTeams} from '../../../../../../store/data/teams/teamsSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {computeIsNormalSelection} from '../../../../../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {AddTopicOrTagDropdown, TagDropdownItem, TagDropdownItemTypesEnum} from './addTopicOrTagDropdown';

/*
 * Props.
 */

interface AddTopicOrTagDropdownContainerProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  /** Potentially partial set of currently selected conversation. */
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  onAddTopicSelect: HandlerOf<FrontEvent> | undefined;
  onEntryPointSelect: HandlerOf<LegacyConversationLinkDropdownEntryPointModel> | undefined;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  interfaceEventSource: ViewerDropdownInterfaceEventSourcesEnum;
}

interface FetchTagsPaginationData {
  hasFetchedInitialItems: boolean;
  offset: number;
}

type AddTopicOrTagStateProps<T> = {
  teams: ResourceMap<TeamModel>;
  userTeams: ReadonlyArray<TeamModel>;
  entryPoints: ReadonlyArray<LegacyConversationLinkDropdownEntryPointModel>;
  conversationIds: ReadonlyArray<number>;
  fetchTagsAsync: T;
};

interface TagFetchResult {
  items: ReadonlyArray<TagDropdownItem>;
  canLoadMore: boolean;
  paginationData: FetchTagsPaginationData;
}
type TagFetchType = (
  query: string,
  tags: ReadonlyArray<TagDropdownItem>,
  cancelToken: CancelToken,
  paginationData: FetchTagsPaginationData,
) => Promise<TagFetchResult>;

type InitialItemsFetchOptions = {
  company: CompanyModel;
  cancelToken: CancelToken;
  conversationTagInboxIds: ReadonlyArray<number> | null;
  entryPoints: ReadonlyArray<LegacyConversationLinkDropdownEntryPointModel>;
  paginationData: FetchTagsPaginationData;
  query: string;
  canAddTopic: boolean;
  canSelectEntryPoint: boolean;
  canFetchRecentTags: boolean;
  user: TeammateModel;
};

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: AddTopicOrTagDropdownContainerProps) {
  return props.conversations;
}

function getCanAddTopic(_state: unknown, props: AddTopicOrTagDropdownContainerProps) {
  return Boolean(props.onAddTopicSelect);
}

function getCanSelectEntryPoint(_state: unknown, props: AddTopicOrTagDropdownContainerProps) {
  return Boolean(props.onEntryPointSelect);
}

function makeGetConversationIds() {
  return createSelector([getConversations], (conversations) => conversations.map((c) => c.id));
}

// Don't display the recently used tags section if ALL conversations are selected using true select all
// and we're not in an inbox view (private inbox or team inbox).
function getCanFetchRecentTags(_state: AppState, props: AddTopicOrTagDropdownContainerProps) {
  if (computeIsNormalSelection(props.inboxesParameters.selection)) {
    return true;
  }

  if (
    props.inboxesParameters.type === InboxesParametersTypesEnum.INBOX ||
    props.inboxesParameters.type === InboxesParametersTypesEnum.TEAMMATE_INBOX
  ) {
    return true;
  }

  return false;
}

function isStaticItemOrInboxTag(item: TagDropdownItem, inboxTagId: number) {
  switch (item.type) {
    case TagDropdownItemTypesEnum.RECENT_TAG_HEADER:
    case TagDropdownItemTypesEnum.ENTRY_POINT_HEADER:
    case TagDropdownItemTypesEnum.ENTRY_POINT_CUSTOM_LINK:
    case TagDropdownItemTypesEnum.SEARCH_TAG_HEADER:
    case TagDropdownItemTypesEnum.EMPTY_LABEL:
    case TagDropdownItemTypesEnum.REQUIRED_TAGGING_HEADER:
      return true;
    case TagDropdownItemTypesEnum.SEARCH_TAG:
      return item.tag[0].id === inboxTagId;
    default:
      return false;
  }
}

async function maybeFetchRecentTagsAsync(
  canFetch: boolean,
  inboxIds: ReadonlyArray<number> | null,
  user: TeammateModel,
  cancelToken: CancelToken,
): Promise<ReadonlyArray<TagDropdownItem>> {
  if (canFetch && inboxIds && inboxIds.length > 0) {
    const recentTagsUrl = addQueryToPath(`${user.url}/recent_tags`, {
      inbox_ids: inboxIds.join(','),
    });
    const fetchedTags = await fetchApiAsync(ApiSearchTags, recentTagsUrl, {cancelToken});

    if (fetchedTags.tags.length > 0) {
      const items: TagDropdownItem[] = fetchedTags.tags.map((tag) => ({
        type: TagDropdownItemTypesEnum.RECENT_TAG,
        tag,
      }));
      const sectionHeader: TagDropdownItem = {type: TagDropdownItemTypesEnum.RECENT_TAG_HEADER};
      return [sectionHeader, ...items];
    }
  }
  return [];
}

async function maybeFetchInitialItemsAsync(options: InitialItemsFetchOptions) {
  if (options.paginationData?.hasFetchedInitialItems || options.query) {
    return [];
  }

  const recentItems = await maybeFetchRecentTagsAsync(
    options.canFetchRecentTags,
    options.conversationTagInboxIds,
    options.user,
    options.cancelToken,
  );
  const entryPointItems = buildEntryPointItems(options);
  return [...recentItems, ...entryPointItems];
}

function buildEntryPointItems(options: InitialItemsFetchOptions): ReadonlyArray<TagDropdownItem> {
  const topicItems: ReadonlyArray<TagDropdownItem> = options.canAddTopic
    ? [{type: TagDropdownItemTypesEnum.ENTRY_POINT_CUSTOM_LINK}]
    : [];

  const entryPointItems: ReadonlyArray<TagDropdownItem> = options.canSelectEntryPoint
    ? options.entryPoints.map(() => ({type: TagDropdownItemTypesEnum.ENTRY_POINT}))
    : [];

  const items = [...topicItems, ...entryPointItems];

  if (items.length === 0) {
    return [];
  }

  return [{type: TagDropdownItemTypesEnum.ENTRY_POINT_HEADER}, ...items];
}

async function fetchRequiredTaggingSafetyNets(
  company: CompanyModel,
  conversations: ReadonlyArray<ConversationBaseModel>,
  cancelToken: CancelToken,
): Promise<ReadonlyArray<SafetyNetRequiredTaggingModel>> {
  // TODO @required-tagging: Handle mass action.
  const requiredTaggingList = (conversations[0]?.safetyNets ?? []).filter(
    isConversationSafetyNetRequiredTagging,
  );

  const shouldFetch = conversations.length === 1 && requiredTaggingList.length !== 0;

  if (!shouldFetch) {
    return [];
  }

  const safetyNetsUrl = addQueryToPath(`${company.url}/safety_nets`, {
    ids: requiredTaggingList.map((requiredTagging) => requiredTagging.id).join(','),
  });

  const safetyNets = await fetchApiAsync(ApiSafetyNets, safetyNetsUrl, {cancelToken});
  return safetyNets.filter((safetyNet) => safetyNet.type === SafetyNetsEnum.REQUIRED_TAGGING);
}

function buildRequiredTaggingItems(
  shouldAddTagsCategoryHeader: boolean,
  requiredTaggingSafetyNets: ReadonlyArray<SafetyNetRequiredTaggingModel>,
): TagDropdownItem[] {
  const hasRequiredTaggingSection = requiredTaggingSafetyNets.length !== 0;

  if (!shouldAddTagsCategoryHeader || !hasRequiredTaggingSection) {
    return [];
  }

  return [
    {type: TagDropdownItemTypesEnum.REQUIRED_TAGGING_HEADER},
    ...requiredTaggingSafetyNets.map(
      (requiredTagging) =>
        ({
          type: TagDropdownItemTypesEnum.REQUIRED_TAGGING,
          requiredTaggingSafetyNet: requiredTagging,
        } as const),
    ),
  ];
}

function makeGetFetchTagsIncludingRecentAsync() {
  const getConversationTagNamespaces = wrapInShallowSelector(
    makeGetConversationTagNamespaces(getConversations),
  );
  const getConversationInboxIds = wrapInShallowSelector(makeGetConversationInboxIdsForTags(getConversations));

  return createSelector(
    [
      getCompany,
      getConversationTagNamespaces,
      getConversationInboxIds,
      getLegacyConversationLinkDropdownEntryPoints,
      getUser,
      getHasPermission,
      getCanAddTopic,
      getCanSelectEntryPoint,
      getCanFetchRecentTags,
      getConversations,
    ],
    (
      company,
      conversationTagNamespaces,
      conversationTagInboxIds,
      entryPoints,
      user,
      hasPermission,
      canAddTopic,
      canSelectEntryPoint,
      canFetchRecentTags,
      conversations,
    ) =>
      async function fetchAsync(
        query: string,
        items: ReadonlyArray<TagDropdownItem>,
        cancelToken: CancelToken,
        paginationData: FetchTagsPaginationData,
      ) {
        const tagNamespaces = conversationTagNamespaces.filter((namespace) =>
          hasPermission(namespace, PermissionsEnum.APPLY_TAG),
        );
        const offset = paginationData?.offset || 0;

        // Build the URL and options.
        const url = buildTagSearchUrl(company, query, {
          offset,
          namespaces: tagNamespaces,
          behaviors: [TagBehaviorsEnum.CAN_APPLY_FROM_LIST],
          inboxIds: conversationTagInboxIds || undefined,
          excludeTagIds: [],
        });

        // Fetch the items.
        const fetchedTagsPromise = fetchApiAsync(ApiSearchTags, url, {cancelToken});
        const initialItemsPromise = maybeFetchInitialItemsAsync({
          company,
          cancelToken,
          conversationTagInboxIds,
          entryPoints,
          paginationData,
          query,
          canAddTopic,
          canSelectEntryPoint,
          canFetchRecentTags,
          user,
        });
        const requiredTaggingSafetyNetsPromise = fetchRequiredTaggingSafetyNets(
          company,
          conversations,
          cancelToken,
        );
        const [initialItems, fetchedTags, requiredTaggingSafetyNets] = await Promise.all([
          initialItemsPromise,
          fetchedTagsPromise,
          requiredTaggingSafetyNetsPromise,
        ]);

        // We need to add the "All tags" header if we're not searching and this is the first fetch.
        const shouldAddTagsCategoryHeader = !query && offset === 0;
        const fetchedItems: TagDropdownItem[] = fetchedTags.tags.map((tag) => ({
          type: TagDropdownItemTypesEnum.SEARCH_TAG,
          tag,
        }));
        // If fetchedItems is [], and this is the first fetch, render a special label for "No tags".
        const fetchedItemsOrLabel: TagDropdownItem[] =
          shouldAddTagsCategoryHeader && fetchedTags.tags.length === 0
            ? [{type: TagDropdownItemTypesEnum.EMPTY_LABEL}]
            : fetchedItems;
        const searchItemsWithLabel: TagDropdownItem[] = shouldAddTagsCategoryHeader
          ? [{type: TagDropdownItemTypesEnum.SEARCH_TAG_HEADER}, ...fetchedItemsOrLabel]
          : fetchedItems;

        const requiredTaggingItems = buildRequiredTaggingItems(
          shouldAddTagsCategoryHeader,
          requiredTaggingSafetyNets,
        );

        const allItems = items.concat(...requiredTaggingItems, ...initialItems, ...searchItemsWithLabel);

        return {
          // Return an empty array if we're not searching and every item is a static item (headers or default rows) or the user's inbox tag,
          // we want to render the empty state in that case.
          items:
            !query && allItems.every((item) => isStaticItemOrInboxTag(item, user.inboxTagId)) ? [] : allItems,
          canLoadMore: fetchedTags.tags.length === fetchedTags.limit,
          paginationData: {
            hasFetchedInitialItems: true,
            offset: offset + fetchedTags.tags.length,
          },
        };
      },
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AddTopicOrTagDropdownContainerProps>()(() => ({
  teams: getTeamsByNamespace,
  userTeams: getUserTeams,
  entryPoints: getLegacyConversationLinkDropdownEntryPoints,
  conversationIds: makeGetConversationIds(),
  fetchTagsAsync: makeGetFetchTagsIncludingRecentAsync(),
}));

const mapDispatchToProps = makeMapDispatchToProps<AddTopicOrTagDropdownContainerProps>()(() => ({
  addTagToConversationsAction,
  removeTagFromConversationsAction,
  bulkAddTagToConversationsAction,
  bulkRemoveTagFromConversationsAction,
}));

function mergeProps<T>(
  stateProps: AddTopicOrTagStateProps<T>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: AddTopicOrTagDropdownContainerProps,
) {
  const {inboxesParameters, cellsContext, onInterfaceEvent} = ownProps;
  const tagConversation: HandlerOf<SearchTagModel> = (searchTag) => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.TAG, false);
    }

    const tag = searchTag[0];
    dispatchProps.addTagToConversationsAction(stateProps.conversationIds, tag.id, searchTag);
    trackEvent(stateProps.conversationIds, tag.id, 'add');
  };

  const untagConversation: HandlerOf<TagModel> = (tag) => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.UNTAG, false);
    }

    dispatchProps.removeTagFromConversationsAction(stateProps.conversationIds, tag.id);
    trackEvent(stateProps.conversationIds, tag.id, 'remove');
  };

  const bulkTagConversation: HandlerOf<SearchTagModel> = (searchTag) => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.TAG, true);
    }

    const tag = searchTag[0];
    dispatchProps.bulkAddTagToConversationsAction(inboxesParameters, cellsContext, tag.id, searchTag);
  };

  const bulkUntagConversation: HandlerOf<TagModel> = (tag) => {
    if (onInterfaceEvent) {
      onInterfaceEvent(BulkActionsTrackingTypesEnum.UNTAG, true);
    }

    dispatchProps.bulkRemoveTagFromConversationsAction(inboxesParameters, cellsContext, tag.id);
  };

  return {
    ...stateProps,
    ...ownProps,
    tagConversation,
    untagConversation,
    bulkTagConversation,
    bulkUntagConversation,
  };
}

export const AddTopicOrTagDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (stateProps, dispatchProps, ownProps) => mergeProps<TagFetchType>(stateProps, dispatchProps, ownProps),
)(AddTopicOrTagDropdown);

/*
 * Tracking.
 */

function trackEvent(conversationIds: ReadonlyArray<number>, tagId: number, action: 'add' | 'remove') {
  trackInterfaceEvent(InterfaceEventsEnum.ADD_REMOVE_TAG, {
    conversations: conversationIds.length <= 1 ? 'single' : 'many',
    tagId,
    action,
  });
}
